import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { selectPagination, fetchGeneratedReports, selectGeneratedReportsTotal, selectLoading, selectGeneratedReports, filterGeneratedReports, deleteReport, bulkDeleteReports } from "./reportsSlice"

import { Link, useParams, useRouteMatch } from "react-router-dom"
import { allObjectValuesEmpty } from "../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnHeaderTitle from "../../components/utilityComponents/table/ColumnHeaderTitle"
import { tableFiltersApplied } from "../../functions/objectFunctions/tableFiltersApplied"
import ColumnSearchFilter from "../../components/utilityComponents/table/ColumnSearchFilter"

import { DownloadOutlined, UploadOutlined, EditOutlined, SearchOutlined, FilterOutlined, FileTextOutlined, DeleteOutlined, FolderOutlined } from "@ant-design/icons"

import { Table, Tooltip, Skeleton, Modal, Button } from "antd"
import path from "path"

import moment from 'moment'
import { selectUserRolesForUser, selectRoles } from "../userRoles/userRolesSlice"
import { fileSize } from "../../lib/string"
import UserClientAssignmentsModal from "../users/UserClientAssignmentsModal"

// Api::V5::GeneratedReportsController#bulk_destroy refuses the whole request over this, so the
// selection is capped here rather than letting the user build one the server will reject.
const BULK_DELETE_LIMIT = 100

export default function GeneratedReports() {
    const dispatch = useDispatch()

    const { url } = useRouteMatch()

    const { clientId } = useParams()

    const [init, setInit] = useState(false)
    const [filters, setFilters] = useState({})
    const [reportToDelete, setReportToDelete] = useState(null)
    const [openModal, setOpenModal] = useState(false)
    const [hovered, setHovered] = useState(false);
    const [selectedIds, setSelectedIds] = useState([])
    const [bulkDeleteMode, setBulkDeleteMode] = useState(false)
    /// `assignmentsUser` is deliberately not cleared on close -- antd animates the modal out, and
    /// nulling it would blank the body mid-animation
    const [assignmentsUser, setAssignmentsUser] = useState(null)
    const [assignmentsOpen, setAssignmentsOpen] = useState(false)
    // Must match the ORDER BY in Api::V5::GeneratedReportsController#index -- page 1 is served
    // by `index`, pages 2+ by `filter`. If these diverge, paginating shuffles the list.
    const [sort, setSort] = useState({
        column: "created_at",
        direction: "desc",
    })

    const pagination = useSelector(selectPagination)
    const total = useSelector(selectGeneratedReportsTotal)
    const loading = useSelector(selectLoading)
    const generatedReports = useSelector(selectGeneratedReports)
    const userRolesForUser = useSelector(selectUserRolesForUser)
    const roles = useSelector(selectRoles)

    useEffect(() => {
        if (init) {
            return
        }

        setInit(true)

        handleFetchGeneratedReports()
    }, [dispatch, init])

    const handleFetchGeneratedReports = () => {
        dispatch(fetchGeneratedReports(clientId, pagination))
    }

    const renderResetFilters = () => {
        if (allObjectValuesEmpty(filters)) {
            return null
        }
        return (
            <div className={"actions-alternate-buttons"}>
                <a
                    href="javascript:;"
                    onClick={() => {
                        handleFetchGeneratedReports()
                        setFilters({})
                        setSelectedIds([])
                        setSort({ column: "created_at", direction: "desc" })
                    }}>
                    <FilterOutlined />
                    Reset Filters
                </a>
            </div>
        )
    }

    // antd's onChange is (pagination, filters, sorter, extra) -- the old 2nd param `sorter` was
    // really antd's `filters`, whose values are `null` for filterDropdown columns, so the branch
    // here never fired. Do NOT "fix" the signature: no column declares an antd `sorter` prop
    // (sorting is custom, via ColumnHeaderTitle -> handleSortGeneratedReportsChange), so antd's
    // real sorter arg is always {} and the fixed branch would route to `index`, silently dropping
    // the user's chosen sort. Always go through `filter` so `sort` survives paging.
    const handleTableChange = (newPagination) => {
        /// the list is server-paginated, so ids from the page we're leaving must not carry forward
        setSelectedIds([])
        handleFilterGeneratedReports(newPagination)
    }

    /// ColumnSearchFilter dispatches filterGeneratedReports itself rather than going through
    /// handleFilterGeneratedReports, so wrapping setFilters is the only hook we get on that path.
    /// It calls setFilters on both apply and clear, so this covers each of them.
    const handleSetFilters = (newFilters) => {
        setSelectedIds([])
        setFilters(newFilters)
    }

    const handleFilterGeneratedReports = (newPagination = null, newSort = null) => {
        return dispatch(
            filterGeneratedReports({
                clientId: clientId,
                filters: filters,
                pagination: !!newPagination ? newPagination : pagination,
                sort: !!newSort ? newSort : sort,
            })
        )
    }

    const renderColumnHeaderTitle = (title) => {
        return <ColumnHeaderTitle title={title} handleSortChange={handleSortGeneratedReportsChange} sort={sort} />
    }

    const handleSortGeneratedReportsChange = (column) => () => {
        let newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        setSort(newSort)
        setSelectedIds([])
        handleFilterGeneratedReports(null, newSort)
    }

    // Shared by the File and Add'l File columns -- identical markup, different attachment.
    const renderFileDownload = (url, bytes) => {
        if (!url) {
            return null
        }

        // `bytes` is null until the API sends file_size/extra_file_size -- fall back to the
        // bare label so this renders correctly against an API that predates that field.
        const size = fileSize(bytes, null)

        return (
            <div className="row-actions" style={{ display: "inline-block" }}>
                <a href={url} target="_blank" rel="noreferrer">
                    <Tooltip title={size ? `Download (${size})` : "Download"}>
                        <FileTextOutlined />
                    </Tooltip>
                </a>
            </div>
        )
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }

    const checkIfCanDelete = () => {
        try {
            return userRolesForUser['generated_report']['destroy']
        } catch (e) {
            return false
        }
    }

    /// true for a deliberately narrow allowlist -- everyone else, super admins included, gets the
    /// page exactly as it was before bulk delete existed
    const checkIfCanBulkDelete = () => {
        try {
            return userRolesForUser["generated_report"]["bulk_destroy"]
        } catch (e) {
            return false
        }
    }

    const canBulkDelete = checkIfCanBulkDelete()

    /// Prefer the server's own answer -- the API ships `client_assignments` on the user permission
    /// alongside the endpoint. Until it does the key is absent and we fall back to the role name, so
    /// this works either side of that change. Once the flag exists it wins outright, false included.
    ///
    /// Both sources are empty on first paint (ConsoleLayout dispatches fetchUserRoleForUser in a
    /// parent effect, which runs after this component's), so this is false for a beat and then flips
    /// -- exactly how the delete and bulk delete affordances above already behave.
    const assignmentsPermission = userRolesForUser?.user?.client_assignments
    const canViewAuthorAssignments = assignmentsPermission === undefined ? roles?.includes("super_admin") : assignmentsPermission

    const openAssignments = (u) => {
        setAssignmentsUser(u)
        setAssignmentsOpen(true)
    }

    const renderCreatedBy = (u) => {
        /// the previous `${u?.first_name} ${u?.last_name}` rendered "undefined undefined" when the
        /// author was missing -- join only the parts that are actually there
        const name = [u?.first_name, u?.last_name].filter(Boolean).join(" ")

        if (!u?.id || !canViewAuthorAssignments) {
            return name || "--"
        }

        return (
            // NOTE tabIndex + role + key handler -- this is the only path to the assignments, so it
            // has to be reachable without a mouse
            <span
                className="created-by-lookup"
                tabIndex={0}
                role="button"
                aria-label={`View client assignments for ${name || `user ${u.id}`}`}
                onClick={() => openAssignments(u)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        openAssignments(u)
                    }
                }}>
                {name || `User ${u.id}`}
            </span>
        )
    }

    /// undefined (rather than a disabled config) so the selection column isn't rendered at all
    /// without the permission
    const rowSelection = canBulkDelete
        ? {
              selectedRowKeys: selectedIds,
              /// sliced defensively -- with per-page selection and a max page size of 100 the cap
              /// isn't reachable today, but a larger page size would make it so
              onChange: (newSelectedRowKeys) => setSelectedIds(newSelectedRowKeys.slice(0, BULK_DELETE_LIMIT)),
              getCheckboxProps: (record) => ({
                  disabled: selectedIds.length >= BULK_DELETE_LIMIT && !selectedIds.includes(record.id),
              }),
              columnWidth: 48,
          }
        : undefined

    const handleCloseDeleteModal = () => {
        setReportToDelete(null)
        setBulkDeleteMode(false)
        setOpenModal(false)
        /// the delete button's hover colour is inline and onMouseLeave never fires once the modal
        /// closes, so without this it reopens stuck in the darker shade
        setHovered(false)
    }

    const handleConfirmDelete = async () => {
        const query = {
            clientId: clientId,
            filters: filters,
            pagination: pagination,
            sort: sort,
        }

        const wasBulk = bulkDeleteMode

        handleCloseDeleteModal()

        if (wasBulk) {
            await dispatch(bulkDeleteReports(selectedIds, query))
            /// cleared only once the request has settled -- those rows are gone from the refreshed page
            setSelectedIds([])
        } else {
            dispatch(deleteReport(reportToDelete.id, query))
        }
    }

    return (
        <div className="muc-table card">
            {/* mounted unconditionally so its per-user cache survives between opens */}
            <UserClientAssignmentsModal user={assignmentsUser} open={assignmentsOpen} onClose={() => setAssignmentsOpen(false)} />
            <Modal
                open={openModal}
                footer={[
                    <Button
                        key="delete"
                        style={{ backgroundColor: hovered ? "#a11220" : "#d11a2a", color: "white" }}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        onClick={handleConfirmDelete}>
                        Delete
                    </Button>,
                    <Button key="cancel" type="back" onClick={handleCloseDeleteModal}>
                        Cancel
                    </Button>,
                ]}
                onCancel={handleCloseDeleteModal}>
                {bulkDeleteMode ? (
                    <>
                        <h3>
                            Delete {selectedIds.length} {selectedIds.length === 1 ? "report" : "reports"}?
                        </h3>
                        <p>
                            This permanently deletes {selectedIds.length === 1 ? "this report" : "these reports"} and removes the downloadable files. It cannot be undone.
                        </p>
                        <div style={{ maxHeight: 200, overflowY: "auto" }}>
                            {generatedReports
                                .filter((gr) => selectedIds.includes(gr.id))
                                .map((gr) => (
                                    <p key={gr.id} style={{ marginBottom: 4 }}>
                                        {gr.name}
                                    </p>
                                ))}
                        </div>
                        <p style={{fontWeight: 'bold'}}>THIS IS IRREVERSIBLE!</p>
                    </>
                ) : (
                    <>
                        <h3>Delete this report?</h3>
                        {reportToDelete && (
                            <>
                                <p>Report: {reportToDelete.report.name}</p>
                                <p>Name: {reportToDelete.name}</p>
                                <p>Assigned to: {reportToDelete.assignable_name}</p>
                                <p>
                                    Created by: {reportToDelete.user.first_name} {reportToDelete.user.last_name}
                                </p>
                                <p style={{fontWeight: 'bold'}}>THIS IS IRREVERSIBLE!</p>
                            </>
                        )}
                    </>
                )}
            </Modal>
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Generated Reports</h2>
                            <div className="badge">{total}</div>
                        </div>
                        <div className="actions">
                            <div className="table-action-links">
                                {canBulkDelete && selectedIds.length > 0 && (
                                    <a
                                        href="javascript:;"
                                        onClick={() => {
                                            setReportToDelete(null)
                                            setBulkDeleteMode(true)
                                            setOpenModal(true)
                                        }}>
                                        <DeleteOutlined />
                                        Delete Selected ({selectedIds.length})
                                    </a>
                                )}

                                {renderResetFilters()}

                                <a href="javascript:;" onClick={() => console.log("Import")}>
                                    <UploadOutlined />
                                    Import
                                </a>

                                <a href="javascript:;" onClick={() => console.log("Export")}>
                                    <DownloadOutlined />
                                    Export
                                </a>
                            </div>
                        </div>
                    </div>
                    <Skeleton active loading={loading}>
                        <Table
                            dataSource={generatedReports}
                            pagination={{ ...pagination, showSizeChanger: true, total: total }}
                            onChange={handleTableChange}
                            rowKey={(r) => r.id}
                            rowSelection={rowSelection}>
                            {/*
                               Document Type,
                               Title,
                               File,
                               Assigned To,
                               Created By,
                               Date Created,
                               Date Updated
                             */}
                            <Table.Column
                                title={renderColumnHeaderTitle("report")}
                                key="report_name"
                                dataIndex="report"
                                render={(gr) => gr?.name}
                                filterDropdown={() => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchGeneratedReports}
                                        filterAction={filterGeneratedReports}
                                        filterColumn={"report_name"}
                                        filterType={"report_name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={handleSetFilters}
                                        sort={sort}
                                        setSort={setSort}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("report_name_cont")}
                            />

                            <Table.Column
                                title={renderColumnHeaderTitle("name")}
                                key="name"
                                dataIndex="name"
                                filterDropdown={() => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchGeneratedReports}
                                        filterAction={filterGeneratedReports}
                                        filterColumn={"name"}
                                        filterType={"name_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={handleSetFilters}
                                        sort={sort}
                                        setSort={setSort}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("name_cont")}
                            />

                            <Table.Column
                                // title={renderColumnHeaderTitle("assignable_name")}
                                title="Assigned To"
                                key="assignable_name"
                                dataIndex="assignable_name"
                                render={(gr) => gr}
                                // filterDropdown={() => (
                                //     <ColumnSearchFilter
                                //         handleFetchInitial={handleFetchGeneratedReports}
                                //         filterAction={filterGeneratedReports}
                                //         filterColumn={"assignable_name"}
                                //         filterType={"assignable_name"}
                                //         filters={filters}
                                //         pagination={pagination}
                                //         setFilters={handleSetFilters}
                                //         sort={sort}
                                //         setSort={setSort}
                                //     />
                                // )}
                                // filterIcon={() => renderSearchOutlinedButton("assignable_name")}
                            />

                            <Table.Column
                                title={renderColumnHeaderTitle("Created By")}
                                key="user"
                                dataIndex="user"
                                render={(u) => renderCreatedBy(u)}
                                filterDropdown={() => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchGeneratedReports}
                                        filterAction={filterGeneratedReports}
                                        filterColumn={"user"}
                                        filterType={"user"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={handleSetFilters}
                                        sort={sort}
                                        setSort={setSort}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("user")}
                            />

                            <Table.Column
                                title={renderColumnHeaderTitle("created_at")}
                                key="created_at"
                                dataIndex="created_at"
                                filterDropdown={() => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchGeneratedReports}
                                        filterAction={filterGeneratedReports}
                                        filterColumn={"created_at"}
                                        filterType={"created_at_eq"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={handleSetFilters}
                                        sort={sort}
                                        setSort={setSort}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("created_at_eq")}
                                render={(text, record) => moment(record.created_at).format("L")}
                            />

                            <Table.Column
                                title={renderColumnHeaderTitle("updated_at")}
                                key="updated_at"
                                dataIndex="updated_at"
                                filterDropdown={() => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchGeneratedReports}
                                        filterAction={filterGeneratedReports}
                                        filterColumn={"updated_at"}
                                        filterType={"updated_at_eq"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={handleSetFilters}
                                        sort={sort}
                                        setSort={setSort}
                                    />
                                )}
                                filterIcon={() => renderSearchOutlinedButton("updated_at_eq")}
                                render={(text, record) => moment(record.updated_at).format("L")}
                            />

                            <Table.Column title="File" key="file" render={(text, record) => renderFileDownload(record.file_url, record.file_size)} />

                            <Table.Column title="Add'l File" key="extra_file" render={(text, record) => renderFileDownload(record.extra_file_url, record.extra_file_size)} />

                            <Table.Column
                                title="Edit"
                                key="action"
                                render={(text, record) => (
                                    <>
                                        <div className="row-actions" style={{ display: "inline-block" }}>
                                            <Link to={path.join(url, "..", `${record.report_id}`, "generated-reports", `${record.id}`, "edit")}>
                                                <Tooltip title="Edit">
                                                    <EditOutlined />
                                                </Tooltip>
                                            </Link>
                                        </div>
                                    </>
                                )}
                            />
                            {checkIfCanDelete() && (
                                <Table.Column
                                    title="Delete"
                                    key="action"
                                    render={(text, record) => (
                                        <>
                                            <Link to="#">
                                                <Tooltip title="Delete">
                                                    <DeleteOutlined
                                                        onClick={() => {
                                                            setBulkDeleteMode(false)
                                                            setReportToDelete(record)
                                                            setOpenModal(true)
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Link>
                                        </>
                                    )}
                                />
                            )}
                        </Table>
                    </Skeleton>
                </div>
            </div>
        </div>
    )
}
